import type { PhotoAnnotation } from '@qms360/shared';
import { apiFetch } from './api.js';

/**
 * App 2 endpoints (spec §10).
 *
 * Kept in its own module rather than appended to `api.ts` because App 2 is the
 * only app with an offline path: these calls are made both directly by screens
 * and by the outbox flush, and having them in one place makes it obvious which
 * ones a device can queue.
 */

export type IssueCategory = 'minor' | 'moderate' | 'major';
export type IssueImpact = 'positive' | 'negative';
export type LessonStatus = 'draft' | 'submitted' | 'sent_back' | 'approved';
export type PhotoCategory = 'before' | 'after';

export interface Lesson {
  id: string;
  referenceNo: string;
  projectId: string;
  businessUnitId: string | null;
  title: string;
  disciplineId: string | null;
  categorisationId: string | null;
  issueCategory: IssueCategory;
  impact: IssueImpact;
  capturedAt: string;
  gpsLatitude: string | null;
  gpsLongitude: string | null;
  gpsAccuracyMetres: string | null;
  gpsCaptureStatus: 'captured' | 'denied' | 'unavailable' | 'timed_out';
  description: string;
  rootCause: string;
  correction: string;
  correctiveAction: string;
  isRepeatedIssue: boolean;
  repeatCount: number | null;
  repeatLocation: string | null;
  status: LessonStatus;
  submitterName: string | null;
  submitterDesignation: string | null;
  submittedAt: string | null;
  approverName: string | null;
  approvedAt: string | null;
  sentBackRemarks: string | null;
  syncState: 'synced' | 'pending' | 'conflict';
  revision: number;
}

export interface LessonPhoto {
  id: string;
  category: PhotoCategory;
  fileId: string;
  caption: string | null;
  capturedAt: string | null;
  /** Marks drawn over the photo, as stored (JSON); read with `parsePhotoAnnotation`. */
  annotation?: string | null;
}

export interface LessonConflict {
  id: string;
  formId: string;
  fieldName: string;
  serverValue: string | null;
  incomingValue: string | null;
  incomingClientAt: string | null;
  resolvedAt: string | null;
}

/** Master data as the pickers need it. `nameAr`/`labelAr` are shown to Arabic readers (pass 12). */
export interface ReferenceLists {
  projects: { id: string; code: string; name: string; nameAr?: string | null; businessUnitId: string | null }[];
  businessUnits: { id: string; code: string; name: string; nameAr?: string | null }[];
  disciplines: { id: string; code: string; name: string; nameAr?: string | null }[];
  categorisations: { id: string; category: string; label: string; labelAr?: string | null }[];
}

export const fetchReference = (app: string) => apiFetch<ReferenceLists>(`/reference/${app}`);

// --- The log ---------------------------------------------------------------

export interface LessonLogFilters {
  projectId?: string;
  issueCategory?: IssueCategory;
  impact?: IssueImpact;
  disciplineId?: string;
  status?: LessonStatus;
  search?: string;
}

export const fetchLessons = (filters: LessonLogFilters = {}) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) query.set(key, value);
  }
  const suffix = query.toString();
  return apiFetch<{ lessons: Lesson[] }>(
    `/lesson-learned/lessons${suffix ? `?${suffix}` : ''}`,
  );
};

export const fetchLesson = (id: string) =>
  apiFetch<{ lesson: Lesson; photos: LessonPhoto[]; conflicts: LessonConflict[] }>(
    `/lesson-learned/lessons/${id}`,
  );

/** `existing`: a lesson with this id was already there — made offline and synced (DATA-6). */
export const createLesson = (body: Record<string, unknown>) =>
  apiFetch<{ lesson: Lesson; existing?: boolean }>('/lesson-learned/lessons', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateLesson = (id: string, body: Record<string, unknown>) =>
  apiFetch<{ lesson: Lesson }>(`/lesson-learned/lessons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// --- Workflow --------------------------------------------------------------

export const submitLesson = (id: string) =>
  apiFetch<{ lesson: Lesson }>(`/lesson-learned/lessons/${id}/submit`, { method: 'POST' });

export const approveLesson = (id: string) =>
  apiFetch<{ lesson: Lesson }>(`/lesson-learned/lessons/${id}/approve`, { method: 'POST' });

export const sendBackLesson = (id: string, remarks: string) =>
  apiFetch<{ lesson: Lesson }>(`/lesson-learned/lessons/${id}/send-back`, {
    method: 'POST',
    body: JSON.stringify({ remarks }),
  });

export const fetchLessonApprovals = () =>
  apiFetch<{ pending: Lesson[] }>('/lesson-learned/approvals');

// --- Photos ----------------------------------------------------------------

/**
 * Upload a file. The server answers `{ id, url }` — the stored file itself,
 * not wrapped. This used to be typed `{ file: { id } }`, so every photo a phone
 * sent failed on `file.id` and was left marked failed (NEW-11).
 */
export const uploadFile = (body: {
  filename: string;
  contentType: string;
  data: string;
  purpose: 'lesson_photo' | 'signature' | 'audit_evidence';
}) => apiFetch<{ id: string; url: string }>('/files', { method: 'POST', body: JSON.stringify(body) });

export const attachPhoto = (
  lessonId: string,
  body: { category: PhotoCategory; fileId: string; caption?: string | null; capturedAt?: string },
) =>
  apiFetch<{ photo: LessonPhoto }>(`/lesson-learned/lessons/${lessonId}/photos`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Draw on a photo, or clear its marks with `null` (pass 8). */
export const annotatePhoto = (lessonId: string, photoId: string, annotation: PhotoAnnotation | null) =>
  apiFetch<{ photo: LessonPhoto }>(`/lesson-learned/lessons/${lessonId}/photos/${photoId}`, {
    method: 'PATCH',
    body: JSON.stringify({ annotation }),
  });

export const archivePhoto = (lessonId: string, photoId: string) =>
  apiFetch<void>(`/lesson-learned/lessons/${lessonId}/photos/${photoId}`, { method: 'DELETE' });

// --- AI rephrasing (ADR-0008) ----------------------------------------------

export type RephrasableField = 'description' | 'rootCause' | 'correction' | 'correctiveAction';

export interface RephraseResult {
  suggestionId: string | null;
  field: RephrasableField;
  original: string;
  text: string | null;
  timedOut: boolean;
  unavailableReason: string | null;
}

// --- Prompt-to-transaction (spec §13.3) -------------------------------------

export interface PromptDraft {
  suggestionId: string | null;
  /** Only what the sentence stated and the server matched. */
  fields: Partial<
    Record<
      | 'title'
      | 'projectId'
      | 'disciplineId'
      | 'issueCategory'
      | 'impact'
      | 'description'
      | 'rootCause'
      | 'correction'
      | 'correctiveAction',
      string
    >
  >;
  clarifications: { field: string; question: string; options?: { value: string; label: string }[] }[];
  timedOut: boolean;
  unavailableReason: string | null;
}

/** A sentence in, a draft of the form out. Creates nothing. */
export const requestPromptDraft = (lessonId: string, prompt: string) =>
  apiFetch<PromptDraft>('/lesson-learned/ai/prompt-drafts', {
    method: 'POST',
    body: JSON.stringify({ lessonId, prompt }),
  });

/** The server judges accepted / edited / rejected from the saved lesson, not from this claim. */
export const disposePromptDraft = (
  suggestionId: string,
  claimed: 'accepted' | 'edited' | 'rejected',
  answers?: Record<string, string>,
) =>
  apiFetch<{ disposition: string }>(`/lesson-learned/ai/prompt-drafts/${suggestionId}/disposition`, {
    method: 'POST',
    body: JSON.stringify({ claimed, ...(answers ? { answers } : {}) }),
  });

export const requestRephrase =(lessonId: string, field: RephrasableField, text?: string) =>
  apiFetch<RephraseResult>(`/lesson-learned/lessons/${lessonId}/rephrase`, {
    method: 'POST',
    // The text on screen, which may not be saved yet — otherwise asking for a
    // rewrite of what you just typed would rewrite the previous version.
    body: JSON.stringify({ field, ...(text === undefined ? {} : { text }) }),
  });

export const saveNarrative = (
  lessonId: string,
  body: {
    field: RephrasableField;
    value: string;
    aiSuggestionId?: string | null;
    aiDisposition?: 'accepted' | 'edited' | 'rejected' | null;
  },
) =>
  apiFetch<{ lesson: Lesson }>(`/lesson-learned/lessons/${lessonId}/narrative`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// --- Sync and conflicts ----------------------------------------------------

export interface SyncOutcome {
  id: string;
  /** `rejected`: the server will not take it from this user — retrying cannot help. */
  status: 'created' | 'merged' | 'conflict' | 'not_found' | 'rejected';
  /** Why, when rejected. */
  reason?: string;
  appliedFields: string[];
  conflictedFields: string[];
}

export interface SyncResult {
  outcomes: SyncOutcome[];
  summary: { created: number; merged: number; conflicted: number; notFound: number };
}

export const pushSyncBatch = (records: unknown[]) =>
  apiFetch<SyncResult>('/lesson-learned/sync', {
    method: 'POST',
    body: JSON.stringify({ records }),
  });

export const fetchConflicts = () =>
  apiFetch<{ conflicts: LessonConflict[] }>('/lesson-learned/conflicts');

export const resolveConflict = (
  id: string,
  body: { choice: 'server' | 'incoming' | 'manual'; value?: string },
) =>
  apiFetch<{ resolvedValue: string }>(`/lesson-learned/conflicts/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
